import { IGeneralStats } from './general';

export interface IPortfolioTransactionResponse {
  status: string;
  currentPage: number;
  nextPage: number;
  previousPage: number;
  totalPages: number;
  totalItems: number;
  transactions: IPortfolioTransaction[];
}

export interface IPortfolioTransactionVerifyResponse {
  status: string;
  transactions: boolean;
}

export interface ICreatePortfolioTransaction {
  id?: number;
  institution_id: number;
  date: string; // "2023-02-28",
  asset_identifier: string;
  quantity: number;
  price: number;
  currency: string;
  brokerage_fee?: number;
  emoluments?: number;
  taxes?: number;
  irrf?: number;
  asset_nicename?: string;
  close_position?: boolean;
}

export interface IPortfolioTransaction {
  amount?: number;
  price?: number;
  asset: string;
  brokerage_fee?: number;
  factor?: number;
  date?: string;
  emoluments?: number;
  id?: number;
  institution_id?: number;
  irrf?: number;
  name: string;
  operation?: PortfolioTransactionOperation;
  quantity?: number;
  source?: 'manual' | 'b3' | 'mais_retorno' | 'planilha';
  taxes?: number;
  type?: string;
  unity_value?: number;
  currency?: string;
  asset_nicename?: string;
  is_approved?: boolean;
  needs_review?: boolean;
  notes?: string;
}

export enum PortfolioTransactionOperation {
  BUY = 'compra',
  SELL = 'venda',
  BONUS = 'compra/bonificacao',
  SPLIT = 'compra/desdobramento',
  GROUP = 'venda/grupamento',
  TICKER_CHANGE = 'venda/ticker_change',
  TICKER_CHANGE_BUY = 'compra/ticker_change',
}

export interface IPortfolioNetWorthResponse {
  current_networth: number;
  total_amount_invested: number;
  gross_return: number;
  total_return: number;
  networth_serie: {
    d: string; // yyyy-mm-dd
    i: number; // Preço Médio
    b: number; // balance (patrimônio)
    c: number; // cash flow (diferença entre aportes e vendas)
  }[];
}

export interface IPortfolioCompositionResponse {
  institutions: Record<
    string,
    {
      current_amount_brl: number;
      portfolio_percentage: number;
    }
  >;
  status: string;
  types: Record<
    string,
    {
      type_result: number;
      current_amount: number;
      current_amount_brl: number;
      portfolio_percentage: number;
      currency: string;
      assets: IPortfolioCompositionAsset[];
    }
  >;
}

export interface IPortfolioCompositionAsset {
  asset_identifier: string;
  last_quote_date: string;
  quantity: number;
  asset: string;
  canonical_url: string | null;
  current_amount: number;
  current_amount_brl: number;
  current_price: number;
  average_price: number;
  result: number;
  return: number;
  portfolio_percentage: number;
  currency: string;
  type_percentage: number;
  paid_incomes: number;
  adjusted_average_price: number;
  quality_flags: {
    code: string;
    type: 'warning' | 'error';
    message: string;
    severity: 'medium' | 'high' | 'low';
    details: {
      end_date: string;
      days_diff: number;
      threshold_days: number;
      last_quote_date: string;
    };
  }[];
}
export interface IPortfolioQuotesResponse {
  quotes: {
    d: string;
    q: number;
    r: number;
    q_twr?: number;
    r_twr?: number;
    q_tir?: number;
    r_tir?: number;
  }[];
  status: string;
}

export interface IPortfolioIncomesScheduleResponse {
  asset_identifier: string;
  last_date_prior: string;
  payment_date: string;
  reference_quantity: number;
  value: number;
  status: string;
  asset_type: string;
}

interface AssetIncome {
  name: string;
  accumulated: number;
  total_percentage: number;
  quantity: number;
  dy: number;
  yoc: number;
}

export interface IPortfolioDividendsByAssetResponse {
  status: string;
  incomes: Record<string, AssetIncome>;
}

interface IncomeByYear {
  [month: string]: number;
  total: number;
  avg: number;
  dy: number;
  yoc: number;
}

export type IPortfolioDividendsByYearResponse = {
  status: string;
  incomes: Record<string, IncomeByYear>;
};

export interface IPortfolioDividendsResponse {
  status: string;
  currentPage: number;
  nextPage: number;
  previousPage: number;
  totalPages: number;
  incomes: {
    type: string;
    total: number;
    value: number;
    status: string;
    payment_date: string; // yyyy-mm-dd
    last_date_prior: string; // yyyy-mm-dd
    asset_identifier: string;
    reference_quantity: number;
    name: string;
    asset_type: string;
  }[];
  totals: {
    Pago: number;
    Provisionado: number;
  };
}

export interface IPortfolioProfitAssetsResponse {
  portfolio_current_profitability: number;
  currentPage: number;
  nextPage: number;
  previousPage: number;
  status: string;
  totalPages: number;
  assets: {
    name: string;
    asset_identifier: string;
    current_price: number;
    average_price: number;
    quantity: number;
    adjusted_average_price: number;
    amount_invested: number;
    return: number;
    adjusted_return: number;
    result: number;
    quotes: { c: number; d: number }[];
  }[];
}

export interface IPortfolioTransactionsByMonthsResponse {
  status: string;
  transactions: Record<
    string,
    Record<
      string,
      {
        buy: number;
        sell: number;
      }
    >
  >;
}

export type PortfolioStats = IGeneralStats;

export interface IPortfolioStatsResponse {
  status: string;
  stats: PortfolioStats;
}

export type IPortfolioIncomesGrouped = Record<string, Record<string, number>>;

export interface IPortfolioIncomesResponse {
  status: string;
  dy: number;
  total: number;
  incomes: Record<string, number> | IPortfolioIncomesGrouped;
}

export interface IPortfolioCreateResponse {
  status: string;
  portfolio_id: string;
}

export interface IPortfolioListResponse {
  portfolios: {
    owner_id: string;
    portfolio_id: string;
    name: string;
    assets_quantity: number;
    is_group: boolean;
    networth: number | null;
    consolidated_at: string;
    user_name: string | null;
    user_email: string | null;
    child_portfolios: {
      portfolio_id: string;
      name: string;
      assets_quantity: number;
    }[];
  }[];
}

export enum PortfolioConsolidationStatus {
  SCHEDULED = 'Agendado',
  IN_PROGRESS = 'Em andamento',
  OLD_IN_PROGRESS = 'Consolidação em progresso',
  IMPORT_B3 = 'Importação em progresso',
  TIMEOUT = 'Tempo limite excedido',
  FINISHED = 'Concluído',
  ERROR = 'Erro no processo de consolidação, tente novamente',
}

export interface IGetPortfolioResponse {
  status: string;
  name: string;
  description: string;
  update_at: string;
  consolidated_at: string | null;
  consolidation_started_at: string | null;
  consolidation_status: PortfolioConsolidationStatus | null;
  assets_quantity: number;
  is_group: boolean;
  child_portfolios: {
    id: string;
    name: string;
    description: string;
    assets_quantity: string;
  }[];
  config?: any;
}

export interface IGetPortfolioSettingsResponse {
  settings: { key: string; value: any }[];
}

export interface IGetTeamPortfolioResponse {
  portfolios: IPortfolioListItem[];
  current: number;
  limit: number;
  canCreate: boolean;
  canBuy: boolean;
}

export interface IPortfolioListItem extends IGetPortfolioResponse {
  portfolio_id: string;
  owner_id: string;
  user_name: string;
  user_email: string;
}

export interface ICreateTransactionByFileResponse {
  status: string;
  logs: { row: number; result: string }[];
}

export interface IUploadTransactionsResponse {
  status: string;
}

export interface IPortfolioVerifyB3AuthResponse {
  is_auth: boolean;
  status: string;
}

export interface IPortfolioVerifyB3AuthParams {
  cpf: string;
  portfolio_id: string;
}

export interface IPortfolioHasB3DocumentResponse {
  cpf: string | null;
  status: string;
}

export interface IPortfolioGetDocumentResponse {
  cpf: string;
  created_at: string;
}

type AssetNicename = [identifier: string, nicename: string];

export interface IPortfolioGetRealIncomeResponse {
  status: string;
  asset_nicenames: AssetNicename[];
}

export enum PortfolioUploadTransactionType {
  ASSET,
  RFS,
}

export enum PortfolioApplicationTag {
  PRIME = 'PRIME',
  PRO = 'PRO',
}

export interface PortfolioJobsParams {
  portfolio_id: string;
  page: number;
  limit: number;
}

export interface IPortfolioJobResponse {
  currentPage: number;
  nextPage: number;
  previousPage: number;
  totalPages: number;
  status: string;
  jobs: {
    type: string;
    status: string;
    date: string;
    details: IPortfolioJobStatistics;
  }[];
}

export interface IPortfolioJobStatistics {
  success_count?: number;
  equities_count?: number;
  treasuries_count?: number;
  job_errors?: IPortfolioJobsStatus[];
  job_warnings?: IPortfolioJobsStatus[];
  date?: string;
  sub_type: string;
}

export interface IPortfolioJobsStatus {
  asset_nicename: string;
  row: number;
  type: string;
}

export interface IPortfolioAssetsPositionsResponse {
  status: string;
  quantity: number;
}

export interface IApproveTransactionParams {
  portfolioId: string;
  isApproved: boolean;
  transactionId: number;
}

export interface IGetCustomerToSharePortfolioResponse {
  integration_id: string;
  portfolio_id: string;
  customer: string;
  type: string;
  integration_enable: boolean;
  link: string;
}

export interface IGetIntegrationFromCustomerResponse {
  id: string;
  type: 'B3';
  portfolio_owner_email: string;
  viewer: string;
  portfolio_id: string;
  created_at: Date;
  integration_enable: boolean;
}

export interface IPortfolioSnapshotsResponse {
  status: string;
  snapshots: {
    id: string;
    date: string;
    created_at: string;
  }[];
}

export interface PortfolioCompositionResponse {
  assets: {
    quantity: number;
    asset: string;
    asset_identifier: string;
    asset_nicename: string;
    canonical_url: string;
    current_amount: number;
    current_amount_brl: number;
    current_price: number;
    average_price: number;
    adjusted_average_price: number;
    result: number;
    return: number;
    portfolio_percentage: number;
    paid_incomes: number;
    currency: string;
    last_quote_date: string;
    type_percentage: number;
    class: string;
    strategy: string;
    liquidity: string;
  }[];
  classes: {
    [className: string]: {
      portfolio_percentage: number;
      current_amount_brl: number;
      result: number;
    };
  };
  strategies: {
    [strategyName: string]: {
      portfolio_percentage: number;
      current_amount_brl: number;
      result: number;
    };
  };
  liquidity: {
    [liquidity: string]: {
      portfolio_percentage: number;
      current_amount_brl: number;
      result: number;
    };
  };
  institutions: {
    [institutionName: string]: {
      current_amount_brl: number;
      portfolio_percentage: number;
    };
  };
}

export type PortfolioIntegrationResponse = {
  portfolio_owner_name: string;
  portfolio_name: string;
  portfolio_assets: number;
  portfolio_updated_at: string;
  id: string;
  portfolio_owner_id: string;
  viewer: string;
  portfolio_id: string;
  created_at: Date;
}[];

export type PortfolioInvite = {
  integration_id: string;
  portfolio_id: string;
  customer: string;
  link: string;
};

export interface GetPortfolioComeCotasResponse {
  status: string;
  currentPage: number;
  page_size: number;
  totalPages: number;
  transactions: PortfolioComeCotasTransaction[];
}

export interface PortfolioComeCotasTransaction {
  id: number;
  date: string;
  asset_identifier: string;
  asset_nicename: string;
  institution_id: number;
  institution_name: string;
  quantity: number;
  price: number;
  taxes: number;
}
